import useBadgeSession, { isBadgeSessionCurrent } from './useBadgeSession';
import { runBadgeCollectionTask, publishBadgeCollection } from './collectionTasks';
import { refreshBenefitQueries } from '../sponsorship/benefitQueries';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../api/api';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import baseline from '../../app-ui/baseline';
import useBadges, { badgeKey } from './useBadges';
import {
  bindBadgeActor,
  captureBadgeActor,
  isBadgeActorCurrent,
  subscribeToBadgeActivity,
} from './progressEvents';
import { readAchievements } from './achievementModel';
import { words } from './model';
import { getLocalizedBadgeName } from './generated/localization';
import timing from './generated/lifecycle.json';
import { BadgeIcon } from './BadgeIcon';
import './achievement.css';
import './badges.css';

export default function BadgeAchievementLifecycle() {
  const session = useBadgeSession();
  const userId = useUserStore((s) => s.user?.id);
  return userId == null ? null : (
    <OwnedAchievements key={session} userId={userId} session={session} />
  );
}
function OwnedAchievements({ userId, session }) {
  useBadges();
  const client = useQueryClient(),
    [notices, setNotices] = useState([]);
  const dismiss = useCallback(() => setNotices((old) => old.slice(1)), []);
  useEffect(() => {
    const release = bindBadgeActor(userId),
      actor = captureBadgeActor(),
      seen = new Set();
    let disposed = false,
      checking = false,
      pending = true,
      debounce,
      idle,
      idleDeadline,
      generation = 0,
      controller;
    const current = () =>
      !disposed &&
      isBadgeSessionCurrent(session) &&
      isBadgeActorCurrent(actor) &&
      useUserStore.getState().user?.id === userId;
    const cancelIdle = () => {
      generation += 1;
      clearTimeout(idleDeadline);
      if (idle == null) return;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      else clearTimeout(idle);
      idle = null;
    };
    const schedule = (delay = timing.debounceMs) => {
      clearTimeout(debounce);
      cancelIdle();
      if (!current() || document.hidden) return;
      debounce = setTimeout(() => {
        const ticket = generation;
        const run = () => {
          if (ticket !== generation) return;
          cancelIdle();
          void check();
        };
        // WebKit/embedded browsers can starve idle callbacks. Keep the native
        // debounce, but bound the browser-only idle wait and discard late callbacks.
        idleDeadline = setTimeout(run, window.requestIdleCallback ? 1000 : 0);
        if (window.requestIdleCallback) idle = window.requestIdleCallback(run, { timeout: 1000 });
      }, delay);
    };
    const check = async () => {
      if (!current() || checking || document.hidden) return;
      checking = true;
      pending = false;
      controller = new AbortController();
      try {
        const credential = localStorage.getItem('POSTPOP_AUTH_TOKEN');
        const requestCurrent = () =>
          current() &&
          !controller.signal.aborted &&
          localStorage.getItem('POSTPOP_AUTH_TOKEN') === credential;
        await runBadgeCollectionTask(client, userId, async () => {
          if (!requestCurrent()) return;
          const result = readAchievements(
            await api.postAuth('/me/badges/check', undefined, {
              signal: controller.signal,
              suppressGlobalError: true,
            }),
          );
          if (!requestCurrent()) return;
          await client.cancelQueries({ queryKey: badgeKey(userId), exact: true });
          if (!requestCurrent()) return;
          const changed = publishBadgeCollection(client, userId, result.collection);
          const next = result.notices.filter((n) => !seen.has(n.key));
          next.forEach((n) => seen.add(n.key));
          if (changed || next.some((n) => n.type === 'badge'))
            void refreshBenefitQueries(client, badgeKey(userId));
          if (next.length) setNotices((old) => [...old, ...next]);
        });
      } catch {
        /* Next successful activity (or next session) can retry. No global alert. */
      } finally {
        checking = false;
        if (pending && current()) schedule(timing.followupMs);
      }
    };
    const unsubscribe = subscribeToBadgeActivity(() => {
      if (!current()) return;
      pending = true;
      schedule();
    });
    const visibility = () => {
      if (document.hidden) {
        clearTimeout(debounce);
        cancelIdle();
      } else if (pending) schedule();
    };
    document.addEventListener('visibilitychange', visibility);
    schedule();
    return () => {
      disposed = true;
      release();
      unsubscribe();
      clearTimeout(debounce);
      cancelIdle();
      controller?.abort();
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [client, userId, session]);
  const notice = notices[0];
  return notice ? <AchievementNotice key={notice.key} notice={notice} onDone={dismiss} /> : null;
}
function blocked() {
  return (
    document.hidden ||
    Array.from(document.querySelectorAll('[role="dialog"]')).some(
      (node) => node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden',
    )
  );
}
function AchievementNotice({ notice, onDone }) {
  const { locale } = useWebLocale(),
    t = words(locale),
    navigate = useNavigate(),
    { resolvedMode } = useColorScheme(),
    c = baseline.tokens[resolvedMode === 'dark' ? 'darkColors' : 'lightColors'];
  const [paused, setPaused] = useState(blocked),
    [held, setHeld] = useState(false),
    [phase, setPhase] = useState('enter'),
    [reduced, setReduced] = useState(
      () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    );
  useEffect(() => {
    const update = () => setPaused(blocked()),
      media = window.matchMedia('(prefers-reduced-motion: reduce)'),
      motion = () => setReduced(media.matches),
      observer = new MutationObserver(update);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'aria-hidden', 'open'],
    });
    document.addEventListener('visibilitychange', update);
    media.addEventListener?.('change', motion);
    update();
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', update);
      media.removeEventListener?.('change', motion);
    };
  }, []);
  useEffect(() => {
    if (paused || held) return;
    setPhase('enter');
    const enter = setTimeout(() => setPhase('shown'), reduced ? 0 : timing.enterMs);
    const hide = setTimeout(() => setPhase('exit'), timing.holdMs);
    const done = setTimeout(onDone, timing.holdMs + (reduced ? 0 : timing.exitMs));
    return () => {
      clearTimeout(enter);
      clearTimeout(hide);
      clearTimeout(done);
    };
  }, [notice, paused, held, reduced, locale, onDone]);
  const badge = notice.type === 'badge',
    heading = t(badge ? 'badges.achievement.unlocked' : 'badges.achievement.goalCompleted'),
    text = badge
      ? `${getLocalizedBadgeName(notice.badge, locale)}${notice.extraCount ? ` +${notice.extraCount}` : ''}`
      : t(
          notice.goal.periodType === 'WEEK'
            ? 'badges.achievement.weekGoalCompleted'
            : 'badges.achievement.monthGoalCompleted',
        );
  const content = (
    <>
      {badge ? (
        <BadgeIcon badge={notice.badge} size={38} />
      ) : (
        <span className="pp-achievement-goal" aria-hidden="true">
          ✓
        </span>
      )}
      <span className="pp-achievement-copy">
        <small>{heading}</small>
        <strong>{text}</strong>
      </span>
    </>
  );
  if (paused) return null;
  return (
    <aside
      className={`pp-achievement ${phase}${reduced ? ' reduced' : ''}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{
        '--achievement-bg': c.surfaceElevated,
        '--achievement-text': c.text,
        '--achievement-border': c.divider,
      }}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setHeld(false);
      }}
    >
      {badge ? (
        <button
          className="pp-achievement-content"
          aria-label={`${heading}: ${text}. ${t('badges.title')}`}
          onClick={() => {
            navigate(`/mypage/badges?focusBadgeKey=${encodeURIComponent(notice.badge.key)}`);
            onDone();
          }}
        >
          {content}
        </button>
      ) : (
        <div className="pp-achievement-content">{content}</div>
      )}
    </aside>
  );
}
